import type { PointArray } from '../types/contour-definition-array-type';

import { vec2 } from 'gl-matrix';

import { EPSILON } from '@/src/constants';

/**
 * Returns a new polyline, snapped to a subdivided grid.
 * @param polyLine - The input polyline to be processed.
 * @param closedContour - Optional to ensure closed loop
 * @param subdivisions - The number of subdivisions for the grid (default is 4).
 * @returns The processed polyline snapped to the grid.
 */
export function snapPolyLineToSubPixelGrid(
  polyLine: PointArray,
  closedContour: boolean = true,
  subdivisions = 4,
): PointArray {
  // Initialize vectors for calculations
  const v1 = vec2.create();
  const v2 = vec2.create();
  const v3 = vec2.create();

  // Set to keep track of seen grid points
  const seen = new Set<string>();
  // Array to store the resulting polyline
  const outputPolyLine: PointArray = [];

  // Process each point in the input polyline
  for (let i = 0; i < polyLine.length; i++) {
    const sx = Math.round(subdivisions * polyLine[i].x);
    const sy = Math.round(subdivisions * polyLine[i].y);
    const key = `${sx};${sy}`;

    // Skip duplicate points

    if (seen.has(key)) continue;

    // Add the new point to the seen set and output polyline
    seen.add(key);
    outputPolyLine.push({
      x: sx / subdivisions,
      y: sy / subdivisions,
    });

    // Check and remove right angles if needed
    if (outputPolyLine.length > 2) {
      const p1 = outputPolyLine[outputPolyLine.length - 3];
      const p2 = outputPolyLine[outputPolyLine.length - 2];
      const p3 = outputPolyLine[outputPolyLine.length - 1];

      vec2.set(v1, p1.x, p1.y);
      vec2.set(v2, p2.x, p2.y);
      vec2.set(v3, p3.x, p3.y);

      vec2.sub(v1, v1, v2);
      vec2.sub(v2, v2, v3);

      // Remove middle point if it forms a right angle
      if (Math.abs(vec2.dot(v1, v2)) < EPSILON) {
        outputPolyLine.splice(-2, 1);
      }
    }
  }

  // Ensure the loop is closed by appending the first point to the end if not already present
  if (outputPolyLine.length > 0 && closedContour) {
    const firstPoint = outputPolyLine[0];
    const lastPoint = outputPolyLine[outputPolyLine.length - 1];
    if (firstPoint.x !== lastPoint.x || firstPoint.y !== lastPoint.y) {
      outputPolyLine.push({ ...firstPoint });
    }
  }

  return outputPolyLine;
}
